import { Dispatch, useMemo } from "react";

import { IApiContext } from "../../components/providers/Api-provider/ApiContext";
import { IStore } from "../store-context";
import { AuthActions, IAuthActions } from "./auth-actions/auth-actions";
import { BulkUploadActions, IBulkUploadActions } from "./bulk-upload-actions/bulk-upload-actions";
import { ClientActions, IClientActions } from "./client-actions/client-actions";
import { ClientWorkspaceActions, IClientWorkspaceActions } from "./client-actions/client-workspace-actions";
import { CommentsActions, ICommentsActions } from "./comments-actions/comments-actions";
import { DataRoomsActions, IDataRoomsActions } from "./data-rooms-actions/data-rooms-actions";
import { AccountActions, IAccountActions } from "./deal-actions/account-actions";
import { AssetClassesActions, IAssetClassesActions } from "./deal-actions/asset-classes-actions";
import { CharacteristicActions, ICharacteristicActions } from "./deal-actions/characteristic-action";
import { CommitmentActions, ICommitmentActions } from "./deal-actions/commitment-actions";
import { DealActions, IDealActions } from "./deal-actions/deal-actions";
import { EmailActions, IEmailActions } from "./deal-actions/email-actions";
import { FileActions, IFileActions } from "./deal-actions/file-actions";
import { FolderActions, IFolderActions } from "./deal-actions/folder-actions";
import { FormActions, IFormActions } from "./deal-actions/forms-action";
import { IInvestorActions, InvestorActions } from "./deal-actions/investor-actions";
import { IMeasureActions, MeasureActions } from "./deal-actions/measure-actions";
import { INoteActions, NoteActions } from "./deal-actions/note-actions";
import { IOpportunitiesActions, OpportunitiesActions } from "./deal-actions/opportunities-actions";
import { IPipelineActions, PipelineActions } from "./deal-actions/pipeline-actions";
import { IRoundActions, RoundActions } from "./deal-actions/round-actions";
import { IShareActions, ShareActions } from "./deal-actions/share-actions";
import { DebtActions, IDebtActions } from "./debt-actions/debt-actions";
import { DocumentsActions, IDocumentsActions } from "./documents-actions/documents-actions";
import { IAction } from "./i-action";
import { INewsFeedActions, NewsFeedActions } from "./news-feed-actions/news-feed-actions";
import {
  IPortfolioConstructionActions,
  PortfolioConstructionActions
} from "./portfolio-construction-actions/portfolio-construction-actions";
import { CashflowActions, ICashflowActions } from "./reports-actions/cashflow-actions";
import { IQuickViewActions, QuickViewActions } from "./reports-actions/quick-view-actions";
import { IReportsActions, ReportsActions } from "./reports-actions/reports-actions";
import { ITaggerActions, TaggerActions } from "./tagger-actions/tagger-actions";
import { ITaskManageActions, TaskManageActions } from "./task-manage-actions/task-manage-actions";
import { DashboardActions, IDashboardActions } from "./user-actions/dashboard-actions";
import { INotificationsActions, NotificationsActions } from "./user-actions/notifications-actions";
import { ISocialActions, SocialActions } from "./user-actions/social-actions";
import { IUserActions, UserActions } from "./user-actions/user-actions";
import { ITeamsActions, TeamsActions } from "./user-manage-actions/teams-actions";
import { IUsersActions, UsersActions } from "./user-manage-actions/users-actions";
import { IViewActions, ViewActions } from "./view-actions/view-actions";
import { EntityTagActions, IEntityTagActions } from "./deal-actions/tag-entity-actions";

export interface IDealModuleActions
  extends IShareActions,
    IInvestorActions,
    IFileActions,
    IOpportunitiesActions,
    IEmailActions,
    IFolderActions,
    ICommitmentActions,
    IRoundActions,
    INoteActions,
    IMeasureActions,
    ICharacteristicActions,
    IFormActions,
    IDealActions,
    IPipelineActions,
    IAssetClassesActions,
    IAccountActions {}

export interface IReportsGroupActions extends IReportsActions, IQuickViewActions, ICashflowActions {}

export interface IClientModuleActions extends IClientActions, IClientWorkspaceActions {}

export interface IUserModuleActions extends IUserActions, ISocialActions, IDashboardActions, INotificationsActions {}

export interface IUserManageActions extends IUsersActions, ITeamsActions {}

interface UseGetActionsParams {
  state: IStore;
  dispatch: Dispatch<IAction>;
  apiContext: IApiContext;
}

export interface IGetActions {
  authActions: IAuthActions;
  userActions: IUserModuleActions;
  dealActions: IDealModuleActions;
  commentsActions: ICommentsActions;
  viewActions: IViewActions;
  reportsActions: IReportsGroupActions;
  debtActions: IDebtActions;
  taskManageActions: ITaskManageActions;
  dataRoomsActions: IDataRoomsActions;
  taggerActions: ITaggerActions;
  documentsActions: IDocumentsActions;
  userManageActions: IUserManageActions;
  portfolioConstructionActions: IPortfolioConstructionActions;
  entityTagActions: IEntityTagActions;
  newsFeedActions: INewsFeedActions;
  bulkUploadActions: IBulkUploadActions;
  clientActions: IClientModuleActions;
}

export const useGetActionsHook = ({ state, dispatch, apiContext: { altiusApi } }: UseGetActionsParams): IGetActions => {
  const { dealModule, userModule } = state;

  const {
    account: { id: accountId }
  } = userModule;
  const {
    deal: { id: dealId, account_id: dealAccountId }
  } = dealModule;

  const isSharedDeal = Boolean(accountId) && Boolean(dealAccountId) && accountId !== dealAccountId;

  const authActions: IAuthActions = useMemo(() => new AuthActions({ dispatch, api: altiusApi }), [altiusApi, dispatch]);

  const userActions: IUserModuleActions = useMemo(
    () => ({
      ...new UserActions({ dispatch, api: altiusApi }),
      ...new DashboardActions({ dispatch, api: altiusApi }),
      ...new SocialActions({ dispatch, api: altiusApi }),
      ...new NotificationsActions({
        dispatch,
        api: altiusApi,
        notificationsPage: userModule.notifications.page,
        dealId: userModule.notifications.filterByDeal ? dealModule.deal?.id : undefined
      })
    }),
    [altiusApi, dispatch, userModule.notifications.page, dealModule.deal?.id, userModule.notifications.filterByDeal]
  );

  const dealActions: IDealModuleActions = useMemo(
    () => ({
      ...new DealActions({ dispatch, api: altiusApi }),
      ...new ShareActions({ dispatch, api: altiusApi }),
      ...new InvestorActions({ dispatch, api: altiusApi }),
      ...new FileActions({ dispatch, api: altiusApi, isSharedDeal }),
      ...new EmailActions({ dispatch, api: altiusApi, isSharedDeal }),
      ...new FolderActions({ dispatch, api: altiusApi, isSharedDeal }),
      ...new CommitmentActions({ dispatch, api: altiusApi }),
      ...new RoundActions({ dispatch, api: altiusApi }),
      ...new NoteActions({ dispatch, api: altiusApi }),
      ...new MeasureActions({ dispatch, api: altiusApi }),
      ...new CharacteristicActions({ dispatch, api: altiusApi }),
      ...new FormActions({ dispatch, api: altiusApi, isSharedDeal }),

      ...new PipelineActions({ dispatch, api: altiusApi }),
      ...new OpportunitiesActions({ dispatch, api: altiusApi }),
      ...new AssetClassesActions({ dispatch, api: altiusApi }),
      ...new AccountActions({ dispatch, api: altiusApi })
    }),
    [altiusApi, dispatch, dealId, isSharedDeal]
  );

  const commentsActions: ICommentsActions = useMemo(
    () => new CommentsActions({ dispatch, api: altiusApi, isSharedDeal }),
    [altiusApi, dispatch, isSharedDeal]
  );

  const viewActions: IViewActions = useMemo(() => new ViewActions({ dispatch, api: altiusApi }), [dispatch, altiusApi]);

  const debtActions: IDebtActions = useMemo(() => new DebtActions({ dispatch, api: altiusApi }), [dispatch, altiusApi]);

  const reportsActions: IReportsGroupActions = useMemo(
    () => ({
      ...new ReportsActions({
        dispatch,
        api: altiusApi,
        deal: {
          id: dealModule.deal?.id,
          title: dealModule.deal?.title
        }
      }),
      ...new QuickViewActions({ dispatch, api: altiusApi }),
      ...new CashflowActions({ dispatch, api: altiusApi })
    }),
    [dispatch, altiusApi, dealModule.deal]
  );

  const taskManageActions: ITaskManageActions = useMemo(
    () =>
      new TaskManageActions({
        dispatch,
        api: altiusApi,
        user: userModule.user
      }),
    [dispatch, altiusApi, userModule.user]
  );

  const dataRoomsActions: IDataRoomsActions = useMemo(
    () => new DataRoomsActions({ dispatch, api: altiusApi }),
    [dispatch, altiusApi]
  );

  const taggerActions: ITaggerActions = useMemo(
    () => new TaggerActions({ dispatch, api: altiusApi }),
    [dispatch, altiusApi]
  );

  const documentsActions: IDocumentsActions = useMemo(
    () => new DocumentsActions({ dispatch, api: altiusApi, dealId, isSharedDeal }),
    [dispatch, altiusApi, dealId, isSharedDeal]
  );

  const userManageActions: IUserManageActions = useMemo(
    () => ({
      ...new UsersActions({ dispatch, api: altiusApi }),
      ...new TeamsActions({ dispatch, api: altiusApi })
    }),
    [altiusApi, dispatch]
  );

  const portfolioConstructionActions: IPortfolioConstructionActions = useMemo(
    () => new PortfolioConstructionActions({ dispatch, api: altiusApi }),
    [dispatch, altiusApi]
  );

  const entityTagActions: IEntityTagActions = useMemo(
    () => new EntityTagActions({ dispatch, api: altiusApi }),
    [dispatch, altiusApi]
  );

  const newsFeedActions: INewsFeedActions = useMemo(
    () => new NewsFeedActions({ dispatch, api: altiusApi }),
    [dispatch, altiusApi]
  );

  const bulkUploadActions: IBulkUploadActions = useMemo(
    () => new BulkUploadActions({ dispatch, api: altiusApi }),
    [dispatch, altiusApi]
  );

  const clientActions: IClientModuleActions = useMemo(
    () => ({
      ...new ClientActions({ dispatch, api: altiusApi }),
      ...new ClientWorkspaceActions({ dispatch, api: altiusApi })
    }),
    [dispatch, altiusApi]
  );

  return {
    authActions,
    userActions,
    dealActions,
    commentsActions,
    viewActions,
    reportsActions,
    debtActions,
    taskManageActions,
    dataRoomsActions,
    taggerActions,
    documentsActions,
    userManageActions,
    portfolioConstructionActions,
    entityTagActions,
    newsFeedActions,
    bulkUploadActions,
    clientActions
  };
};

export * from "./actions";
